/** Elevation profile (km → m) with surface-coloured segments and climb shading. */
import { Line } from "react-chartjs-2";
import type { ChartData, ChartOptions, ScriptableLineSegmentContext } from "chart.js";
import type { Analysis } from "@/api/schemas";
import { SURFACE_META, surfaceColor } from "@/lib/surface";
import { alpha, baseOptions, palette, useChartTheme } from "./charts";

export function ElevationProfile({
  analysis,
  height = 200,
}: {
  analysis: Pick<Analysis, "profile" | "climbs">;
  height?: number;
}) {
  useChartTheme(); // re-read the colours when the theme changes
  const pts = analysis.profile;
  if (pts.length < 2) return null;
  const p = palette();
  const surfaces = pts.map((x) => x[2]);
  const data: ChartData<"line", { x: number; y: number }[]> = {
    datasets: [
      {
        label: "Elevation",
        data: pts.map(([x, y]) => ({ x, y })),
        borderWidth: 2.5,
        pointRadius: 0,
        tension: 0, // measured data: no smoothing (a spline invents bumps)
        fill: "start",
        backgroundColor: alpha(p.accent, 0.12),
        borderColor: p.accent,
        segment: {
          borderColor: (ctx: ScriptableLineSegmentContext) => {
            const s = surfaces[ctx.p0DataIndex];
            return s ? surfaceColor(s) : p.accent;
          },
        },
      },
    ],
  };
  const options = baseOptions({
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items: { parsed: { x: number } }[]) => `${items[0]?.parsed.x.toFixed(1)} km`,
          label: (item: { parsed: { y: number }; dataIndex: number }) => {
            const s = surfaces[item.dataIndex];
            return `${Math.round(item.parsed.y)} m${s ? ` · ${SURFACE_META[s].label}` : ""}`;
          },
        },
      },
    },
    scales: {
      x: {
        type: "linear",
        min: 0,
        max: pts[pts.length - 1]![0],
        ticks: { callback: (v: number | string) => `${Math.round(Number(v))} km`, maxTicksLimit: 8 },
      },
      y: { ticks: { callback: (v: number | string) => `${v} m`, maxTicksLimit: 5 } },
    },
  } as unknown as Partial<ChartOptions<"line">>) as ChartOptions<"line">;
  return (
    <div className="chart" style={{ height }} role="img" aria-label="Elevation profile">
      <Line data={data} options={options} />
    </div>
  );
}
